import { PlatformSourceMapper } from '@mappers'
import { mapPropertyOrDefault } from '@mappers/mapping-helper'
import { getKeyByValue, swapKeysWithValues } from '@utils/functionalBob'
import ReturnActions, { ReturnActionsEnum } from '@enums/modules/products/returnActions'

export const packageTypeEnumMap = {
  0: 'None',
  1: 'Box',
  2: 'Bubble Mailer',
  3: 'Poly Mailer',
  5: 'Poster Tube',
  6: 'Custom Packaging (beta)',
  7: 'Bookfold - Large',
  8: 'Ship in Own'
}

export const customPackageTypeEnumMap = {
  0: 'None',
  1: 'Box',
  2: 'Bubble Mailer',
  3: 'Poly Mailer'
}

export const packageTypeDescriptionEnumMap = {
  0: 'None',
  1: 'Box',
  2: 'Bubble Mailer',
  3: 'Poly Mailer',
  5: 'Poster Tube',
  6: 'You still need to set rules/bundles, but this will help us ensure it is handled the way you want and in the future, build on this feature.',
  7: 'Bookfold - Large',
  8: 'Ship in Own Container'
}

export const apparelTypeEnumMap = {
  1: 'Jacket',
  2: 'Shirt',
  3: 'Sweatshirt',
  4: 'Pants',
  5: 'Other'
}

export const customPackingType = {
  5: 'Box',
  13: 'Bubble Mailer',
  14: 'Poly Mailer'
}

export const returnActionsEnumMap = swapKeysWithValues(ReturnActionsEnum)

const mapProductDimensionsFromAPI = ({
  length,
  width,
  height
}) => ({
  length,
  width,
  height
})

const mapProductAmazonFbaFromAPI = ({
  asin,
  fnsku
}) => ({
  asin: asin ?? '',
  fnsku: fnsku ?? ''
})

const mapProductWeightFromAPI = ({ weight }) => {
  const defaultWeight = {
    pounds: 0,
    ounces: 0
  }

  if (!weight || isNaN(weight)) {
    return defaultWeight
  }

  return {
    pounds: Math.floor(weight / 16),
    ounces: weight % 16
  }
}

// Cannot destructure 'default' from returnActions here as 'default' is a keyword.
const mapReturnActionsFromApi = (returnActionsVm) => ({
  action: getKeyByValue(ReturnActionsEnum, returnActionsVm.default),
  backup: returnActionsVm.backup ? getKeyByValue(ReturnActionsEnum, returnActionsVm.backup) : ReturnActions.Dispose,
  instructions: returnActionsVm.instructions
})

const mapReturnToSenderActionsFromApi = (returnToSenderActions) => ({
  action: getKeyByValue(ReturnActionsEnum, returnToSenderActions.defaultAction),
  backup: returnToSenderActions.backupAction ? getKeyByValue(ReturnActionsEnum, returnToSenderActions.backupAction) : ReturnActions.Dispose
})

const mapReturnActionsToApi = ({ action, backup, instructions = '' }) => ({
  default: getKeyByValue(returnActionsEnumMap, action),
  backup: action === ReturnActions.Restock ? getKeyByValue(returnActionsEnumMap, backup) : null,
  instructions: action === ReturnActions.Restock ? instructions : ''
})

const mapReturnToSenderActionsToApi = ({ action, backup }) => ({
  defaultAction: getKeyByValue(returnActionsEnumMap, action),
  backupAction: action === ReturnActions.Restock ? getKeyByValue(returnActionsEnumMap, backup) : null
})

const mapSelectedProductDescription = (productDescriptionAttributesVM, apparelTypeVm, packageMaterialVm) => {
  const selectedProductDescription = { id: 0, name: 'isNoRequirements' }
  if (productDescriptionAttributesVM && productDescriptionAttributesVM.productsDescriptionAttributes && productDescriptionAttributesVM.productsDescriptionAttributes.length > 0) {
    const excludeIrrelevantAttributes = productDescriptionAttributesVM.productsDescriptionAttributes.filter(x => x.id !== 8 && x.id !== 11 && x.id !== 16)
    if (excludeIrrelevantAttributes.length > 0) {
      selectedProductDescription.id = excludeIrrelevantAttributes[0].id
      selectedProductDescription.name = excludeIrrelevantAttributes[0].name
    }
  }
  if (selectedProductDescription.id === 0) {
    if (packageMaterialVm && packageMaterialVm.id === -1) {
      selectedProductDescription.id = -1
      selectedProductDescription.name = 'isSpecial'
    } else if (apparelTypeVm && apparelTypeVm.id !== 0) {
      selectedProductDescription.id = 3
      selectedProductDescription.name = 'isApparel'
    }
  }
  if (selectedProductDescription.id === 13 || selectedProductDescription.id === 14) {
    selectedProductDescription.id = 5
    selectedProductDescription.name = 'isCustomPackaging'
  }
  return selectedProductDescription
}

const mapProductDescriptionAttributesFromSelectedProductDescriptionToAPI = (productDescriptionAttributes, selectedProductDescriptionAttribute) => {
  if (!selectedProductDescriptionAttribute.id === 5 && (!productDescriptionAttributes.isCustomBubbleMailer && !productDescriptionAttributes.isCustomPolyMailer)) {
    for (const key in productDescriptionAttributes) {
      // Maintain state of product description attributes when packing requirements change
      if (key !== 'isCasePick' && key !== 'isProp65Qualified' && key !== 'isShipInOwnContainer') productDescriptionAttributes[key] = false
    }
    productDescriptionAttributes[selectedProductDescriptionAttribute.name] = true
  }
  if (selectedProductDescriptionAttribute.id === 5 && (!productDescriptionAttributes.isCustomBubbleMailer && !productDescriptionAttributes.isCustomPolyMailer)) {
    for (const key2 in productDescriptionAttributes) {
      // Maintain state of product description attributes when packing requirements change
      if (key2 !== 'isCasePick' && key2 !== 'isProp65Qualified' && key2 !== 'isShipInOwnContainer') productDescriptionAttributes[key2] = false
    }
    productDescriptionAttributes[selectedProductDescriptionAttribute.name] = true
  }
  mapPropertyOrDefault(mapProductDescriptionAttributesToAPI, productDescriptionAttributes)
  return productDescriptionAttributes
}

const mapProductWeightToAPI = (weight) => {
  const defaultWeight = 0
  let poundsInOunces = 0
  let ounces = 0

  if (!weight) {
    return defaultWeight
  }

  if (weight.pounds) {
    const parsedPounds = isNaN(parseFloat(weight.pounds)) ? 0 : parseFloat(weight.pounds)
    poundsInOunces = parsedPounds * 16
  }

  if (weight.ounces) {
    ounces = isNaN(parseFloat(weight.ounces)) ? 0 : parseFloat(weight.ounces)
  }

  return poundsInOunces + ounces
}

const mapProductDimensionsAndWeightToAPI = ({
  length,
  width,
  height
}, weight) => ({
  length: parseFloat(length),
  width: parseFloat(width),
  height: parseFloat(height),
  weight: mapProductWeightToAPI(weight)
})

const mapProductStatusAttributesFromAPI = ({
  isActive,
  isDigital,
  isLot,
  isDangerousGoods,
  hasSerialNumber,
  hasBeenCubiScanned,
  suffix,
  prefix,
  exactSerialCharLength,
  minimumShelfLifeDays
}) => ({
  isActive,
  isDigital,
  isLot,
  isDangerousGoods,
  hasSerialNumber,
  hasBeenCubiScanned,
  suffix,
  prefix,
  exactSerialCharLength,
  hasMinimumShelfLife: !!minimumShelfLifeDays,
  minimumShelfLifeDays: minimumShelfLifeDays ?? undefined
})

const mapProductStatusAttributesToAPI = ({
  isActive,
  isDigital,
  isLot,
  isDangerousGoods,
  hasSerialNumber,
  hasBeenCubiScanned,
  suffix,
  prefix,
  exactSerialCharLength,
  hasMinimumShelfLife,
  minimumShelfLifeDays
}) => ({
  isActive,
  isDigital,
  isLot,
  isDangerousGoods,
  hasSerialNumber,
  hasBeenCubiScanned,
  suffix,
  prefix,
  exactSerialCharLength,
  minimumShelfLifeDays: hasMinimumShelfLife ? minimumShelfLifeDays : undefined
})

const mapProductDescriptionAttributesFromAPI = ({
  isFragile,
  isFoldable,
  isStackable,
  isBook,
  isMedia,
  isCustomPackaging,
  isCustomDunnage,
  isMarketingInsert,
  isCasePick,
  isProp65Qualified,
  isProp65Label,
  isCustomBubbleMailer,
  isCustomPolyMailer,
  isShipInOwnContainer
}) => ({
  isFragile,
  isFoldable,
  isStackable,
  isBook,
  isMedia,
  isCustomPackaging,
  isCustomDunnage,
  isMarketingInsert,
  isCasePick,
  isProp65Qualified,
  isProp65Label,
  isCustomBubbleMailer,
  isCustomPolyMailer,
  isShipInOwnContainer
})

const mapProductDescriptionAttributesToAPI = ({
  isFragile,
  isFoldable,
  isStackable,
  isBook,
  isMedia,
  isCustomPackaging,
  isCustomDunnage,
  isMarketingInsert,
  isCasePick,
  isProp65Qualified,
  isProp65Label,
  isCustomBubbleMailer,
  isCustomPolyMailer,
  isShipInOwnContainer
}) => ({
  isFragile,
  isFoldable,
  isStackable,
  isBook,
  isMedia,
  isCustomPackaging,
  isCustomDunnage,
  isMarketingInsert,
  isCasePick,
  isProp65Qualified,
  isProp65Label,
  isCustomBubbleMailer,
  isCustomPolyMailer,
  isShipInOwnContainer
})

const mapPackageMaterialFromAPI = ({
  id
}) => ({
  id,
  name: packageTypeEnumMap[id]
})

const mapPackageMaterialToAPI = ({
  id,
  name
}) => ({
  id,
  name
})

const mapApparelTypeBasedonDefaultFromAPI = (apparelTypeVm) => {
  if (apparelTypeVm && apparelTypeVm.id === 0) return { id: 5, name: 'Other' }
  return apparelTypeVm
}

const mapCustomPackagingTypeDefaultFromAPI = (customPackagingTypeVM) => {
  if (customPackagingTypeVM && customPackagingTypeVM.id === 0) return { id: 5, name: 'Box' }
  if (customPackagingTypeVM && customPackagingTypeVM.id === 5) return { id: 5, name: 'Box' }
  if (customPackagingTypeVM && customPackagingTypeVM.id === 13) return { id: 13, name: 'Bubble Mailer' }
  if (customPackagingTypeVM && customPackagingTypeVM.id === 14) return { id: 14, name: 'Poly Mailer' }
  return customPackagingTypeVM
}

const mapApparelTypeFromAPI = ({
  id,
  name
}) => ({
  id,
  name: name || apparelTypeEnumMap[id]
})

const mapCustomPackagingTypeFromAPI = ({
  id,
  name
}) => ({
  id,
  name: name || customPackingType[id]
})

const mapApparelTypeBasedOnSelectedProductAttributeToAPI = (apparelType, selectedProductDescriptionAttribute) => {
  if (selectedProductDescriptionAttribute.id === 3) return apparelType
  return { id: 0, name: 'None' }
}

const mapApparelTypeToAPI = ({
  id
}) => ({
  id
})

const mapCustomsInformationFromAPI = ({
  tarrifCode,
  itemValue,
  itemDescription
}) => ({
  tariffCode: tarrifCode,
  itemValue,
  itemDescription
})

const mapCustomsInformationToAPI = ({
  tariffCode,
  itemValue,
  itemDescription
}) => ({
  tarrifCode: tariffCode,
  itemValue: parseFloat(itemValue),
  itemDescription
})

const mapBundleChildFromApi = ({
  bundleRootId,
  id,
  productDetails,
  quantity,
  updatedByUserId
}) => ({
  bundleRootId,
  id,
  product: mapProductFromAPI(productDetails),
  quantity,
  updatedByUserId
})

const mapProductsToAddInOrderFromApi = ({
  id,
  productDetails,
  quantity
}) => ({
  id,
  product: mapProductFromAPI(productDetails),
  quantity
})

const mapBundleChildrenFromApi = (childrenObj) => {
  const children = childrenObj && childrenObj.children
  let mappedChildren = []
  if (children) mappedChildren = children.map(mapBundleChildFromApi)
  return {
    children: mappedChildren
  }
}

const mapBundleParentFromApi = ({
  productId,
  name,
  platformId,
  quantity
}) => ({
  productId,
  name,
  platformSource: mapPropertyOrDefault(PlatformSourceMapper.mapPlatformSourceFromAPI, platformId).name,
  quantity
})

const mapBundleParentsFromApi = (parentsObj) => {
  const parents = parentsObj && parentsObj.parents
  let mappedParents = []
  if (parents) mappedParents = parents.map(mapBundleParentFromApi)
  return {
    parents: mappedParents
  }
}

const mapMergeParentFromApi = (parentProduct) => {
  return {
    id: parentProduct.id,
    type: parentProduct.type,
    name: parentProduct.name,
    productStatusAttributes: mapProductStatusAttributesFromAPI(parentProduct.productStatusAttributeVm),
    platformId: parentProduct.platformId,
    platformSource: mapPropertyOrDefault(PlatformSourceMapper.mapPlatformSourceFromAPI, parentProduct.platformId).name,
    sku: parentProduct.sku
  }
}

const mapMergedProductFromApi = (mergedObj) => {
  if (mergedObj === null) {
    return {
      parentProduct: null,
      childrenProduct: null
    }
  }
  const childrenProduct = mergedObj.childrenProduct
  let mappedChildren = []
  const mappedParent = childrenProduct === null ? mapMergeParentFromApi(mergedObj.parentProduct) : null
  if (childrenProduct) mappedChildren = childrenProduct.map(mapMergedChildFromApi)
  return {
    parentProduct: mappedParent,
    childrenProduct: mappedChildren
  }
}

const mapMergedChildFromApi = ({
  type,
  productStatusAttributeVm,
  id,
  name,
  platformId,
  sku
}) => ({
  type,
  status: mapProductStatusAttributesFromAPI(productStatusAttributeVm).isActive,
  id,
  name,
  platformSource: mapPropertyOrDefault(PlatformSourceMapper.mapPlatformSourceFromAPI, platformId).name,
  sku
})
let _pageSize = 100
let _pageNumber = 1
const mapProductsFromAPI = {
  toApi: ({ userId, filters, pageSize, pageNumber }) => {
    _pageSize = pageSize
    _pageNumber = pageNumber
    return {
      userId,
      filters: filters.map(FilterMapper.toApi),
      pageSize,
      pageNumber
    }
  },
  fromApi: ({ products, pagination }) => {
    const count = pagination.totalCount
    return {
      products: products.map(mapProductFromAPI),
      pagination: {
        pageNumber: _pageNumber,
        pageSize: count,
        totalCount: count,
        totalPages: Math.ceil(count / _pageSize)
      }
    }
  }
}

const FilterMapper = {
  toApi: ({
    FilterType,
    Operator,
    Value
  }) => ({
    FilterType,
    Operator,
    Value
  })
}

const mapProductFromAPI = ({
  id,
  name,
  platformId,
  barCode,
  reorderPoint,
  sku,
  storeSku,
  platformProductAsin,
  platformProductFnsku,
  imageUrl,
  msdsFileUrl,
  measurementsVm,
  productStatusAttributeVm,
  productDescriptionAttributesVM,
  packageMaterialVm,
  returnActionsVm,
  returnToSender,
  apparelTypeVm,
  customsInformationVm,
  bundleChildrenVm,
  bundleParentsVm,
  mergedProductVm,
  customPackagingTypeVM
}) => ({
  id,
  name,
  platformSource: mapPropertyOrDefault(PlatformSourceMapper.mapPlatformSourceFromAPI, platformId).name,
  platformId,
  barCode,
  reorderPoint,
  sku,
  storeSku: !storeSku ? '-' : storeSku,
  amazonFba: mapProductAmazonFbaFromAPI({ asin: platformProductAsin, fnsku: platformProductFnsku }),
  imageUrl,
  msdsFileUrl,
  availableBoxTypes: [
    { id: 0, name: packageTypeEnumMap[0], description: packageTypeDescriptionEnumMap[0] },
    { id: 1, name: packageTypeEnumMap[1], description: packageTypeDescriptionEnumMap[1] },
    { id: 2, name: packageTypeEnumMap[2], description: packageTypeDescriptionEnumMap[2] },
    { id: 3, name: packageTypeEnumMap[3], description: packageTypeDescriptionEnumMap[3] },
    { id: 5, name: packageTypeEnumMap[5], description: packageTypeDescriptionEnumMap[5] },
    { id: 6, name: packageTypeEnumMap[6], description: packageTypeDescriptionEnumMap[6] },
    { id: 7, name: packageTypeEnumMap[7], description: packageTypeDescriptionEnumMap[7] },
    { id: 8, name: packageTypeEnumMap[8], description: packageTypeDescriptionEnumMap[8] }
  ],
  availableApparelTypes: [
    { id: 1, name: apparelTypeEnumMap[1] },
    { id: 2, name: apparelTypeEnumMap[2] },
    { id: 3, name: apparelTypeEnumMap[3] },
    { id: 4, name: apparelTypeEnumMap[4] },
    { id: 5, name: apparelTypeEnumMap[5] }
  ],
  availableCustomPackingTypes: [
    { id: 5, name: customPackageTypeEnumMap[1] },
    { id: 13, name: customPackageTypeEnumMap[2] },
    { id: 14, name: customPackageTypeEnumMap[3] }
  ],

  selectedProductDescriptionAttribute: mapSelectedProductDescription(productDescriptionAttributesVM, apparelTypeVm, packageMaterialVm),
  dimensions: mapPropertyOrDefault(mapProductDimensionsFromAPI, measurementsVm),
  weight: mapPropertyOrDefault(mapProductWeightFromAPI, measurementsVm),
  productStatusAttributes: mapPropertyOrDefault(mapProductStatusAttributesFromAPI, productStatusAttributeVm),
  productDescriptionAttributes: mapPropertyOrDefault(mapProductDescriptionAttributesFromAPI, productDescriptionAttributesVM),
  packageMaterial: mapPropertyOrDefault(mapPackageMaterialFromAPI, packageMaterialVm),
  returnPreferences: mapPropertyOrDefault(mapReturnActionsFromApi, returnActionsVm),
  returnToSenderPreferences: mapPropertyOrDefault(mapReturnToSenderActionsFromApi, returnToSender),
  apparelType: mapPropertyOrDefault(mapApparelTypeFromAPI, mapApparelTypeBasedonDefaultFromAPI(apparelTypeVm)),
  customPackType: mapPropertyOrDefault(mapCustomPackagingTypeFromAPI, mapCustomPackagingTypeDefaultFromAPI(customPackagingTypeVM)),
  customsInformation: mapPropertyOrDefault(mapCustomsInformationFromAPI, customsInformationVm),
  bundleChildren: mapBundleChildrenFromApi(bundleChildrenVm),
  bundleParents: mapBundleParentsFromApi(bundleParentsVm),
  mergedProduct: mapMergedProductFromApi(mergedProductVm)
})

const mapProductToAPI = ({
  id,
  name,
  platformId,
  barCode,
  reorderPoint,
  sku,
  imageUrl,
  dimensions,
  weight,
  productStatusAttributes,
  productDescriptionAttributes,
  selectedProductDescriptionAttribute,
  packageMaterial,
  returnPreferences,
  returnToSenderPreferences,
  apparelType,
  customsInformation,
  amazonFba
}) => {
  const customsInformationVm = mapPropertyOrDefault(mapCustomsInformationToAPI, customsInformation)

  return {
    id,
    name,
    platformId,
    barCode,
    itemDescription: customsInformationVm.itemDescription,
    reorderPoint: parseInt(reorderPoint),
    sku,
    imageUrl,
    measurementsVm: mapProductDimensionsAndWeightToAPI(dimensions, weight),
    productStatusAttributeVm: mapPropertyOrDefault(mapProductStatusAttributesToAPI, productStatusAttributes),
    productDescriptionAttributes: mapProductDescriptionAttributesFromSelectedProductDescriptionToAPI(productDescriptionAttributes, selectedProductDescriptionAttribute),
    packageMaterialVm: mapPropertyOrDefault(mapPackageMaterialToAPI, packageMaterial),
    returnActionsVm: mapReturnActionsToApi(returnPreferences),
    returnToSender: mapReturnToSenderActionsToApi(returnToSenderPreferences),
    apparelTypeVm: mapPropertyOrDefault(mapApparelTypeToAPI, mapApparelTypeBasedOnSelectedProductAttributeToAPI(apparelType, selectedProductDescriptionAttribute)),
    customsInformationVm,
    platformProductAsin: amazonFba?.asin,
    platformProductFnsku: amazonFba?.fnsku
  }
}

const mapForUnmergeToAPI = ({ childId, rootId }) => ({ ProductToUnmergeId: childId, ProductToUnmergeRootId: rootId })

export default {
  mapBundleChildFromApi,
  mapProductsFromAPI,
  mapProductFromAPI,
  mapProductToAPI,
  mapForUnmergeToAPI,
  mapProductsToAddInOrderFromApi
}
